import { Country } from '@/lib/enums/Country'
import { PolygonType } from '@/lib/enums/PolygonType'
import { Timezone } from '@/lib/enums/Timezone'
import { deliveryCutoffOptions } from '@/lib/delivery-cutoff-options'
import { idSchema } from '@/lib/utils'
import { z } from 'zod/v4'

export const locationIdSchema = z.object({ locationId: idSchema('location') })

const timezoneValues = Object.values(Timezone).map((tz) => tz.Value)
const countryValues = Object.values(Country).map((country) => country.Value)
const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const hourPattern = /^([01]\d|2[0-3]):[0-5]\d$/
const googleBusinessProfileHosts = new Set([
  'search.google.com',
  'share.google',
  'maps.app.goo.gl',
  'g.page',
  'maps.google.com',
  'www.google.com',
  'google.com',
])

function isGoogleBusinessProfileUrl(value: string) {
  if (!value) {
    return true
  }

  try {
    const url = new URL(value)
    const hostname = url.hostname.toLowerCase()
    const pathname = safeDecodeUrlPart(url.pathname).toLowerCase()
    const isGoogleSearchHost = hostname === 'google.com' || hostname === 'www.google.com'
    const isGoogleReviewRequestHost = hostname === 'search.google.com'

    if (!googleBusinessProfileHosts.has(hostname)) {
      return false
    }

    if (isGoogleReviewRequestHost) {
      return pathname === '/local/writereview' && Boolean(url.searchParams.get('placeid')?.trim())
    }

    return !isGoogleSearchHost || url.pathname.toLowerCase().startsWith('/maps/')
  } catch {
    return false
  }
}

const emptyNumberInputToUndefined = (value: unknown) =>
  value === '' || value === null || value === undefined ? undefined : value
const offeredDeliveryCutoffHours = new Set(deliveryCutoffOptions.map(({ id }) => id))

const nullableDeliveryCutoff = z.preprocess(
  (value) => (value === '' || value === undefined ? null : value),
  z.coerce
    .number()
    .int()
    .refine((value) => offeredDeliveryCutoffHours.has(value), {
      message: 'Select a cutoff offered for the location',
    })
    .nullable()
)

const requiredNonnegativeNumber = (requiredMessage: string, minMessage: string) =>
  z.preprocess(
    emptyNumberInputToUndefined,
    z.coerce.number({ error: requiredMessage }).min(0, minMessage)
  )

const toFiniteNumber = (value: unknown) => {
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

interface DistanceRangeBounds {
  index: number
  min: number | null
  max: number | null
}

const isCompleteDistanceRange = (
  range: DistanceRangeBounds
): range is DistanceRangeBounds & { min: number; max: number } =>
  range.min !== null && range.max !== null

const formatMiles = (value: number) =>
  String(Number(Math.round((value + Number.EPSILON) * 10) / 10))

function safeDecodeUrlPart(value: string) {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export const locationAddressSchema = z.object({
  name: z.string().max(255).nullish(),
  line1: z.string().trim().min(1, 'Address is required').max(255),
  line2: z.string().max(255).nullish(),
  city: z.string().trim().min(1, 'City is required').max(255),
  state: z.string().trim().min(1, 'State is required').max(255),
  postal_code_suffix: z.string().nullish(),
  postal_code: z.string().trim().min(1, 'Postal code is required').max(20),
  country: z.enum(countryValues).default(Country.UnitedStates.Value),
  timezone: z.enum(timezoneValues, { message: 'Please select a valid timezone' }),
  lat: z.number().min(-90).max(90).nullable().optional(),
  lng: z.number().min(-180).max(180).nullable().optional(),
  google_place_id: z.string().nullish(),
  is_verified: z.boolean().nullish(),
})

export const deliveryPostalCodeFeeSchema = z.object({
  id: z.union([z.number(), z.string()]).optional(),
  postal_code: z
    .string()
    .trim()
    .regex(/^\d{5}$/, 'Postal code must be exactly 5 digits'),
  fee: z.coerce.number().min(0, 'Delivery fee must be 0 or greater'),
  delivery_cutoff: nullableDeliveryCutoff.default(null),
})

export const deliveryCityFeeSchema = z.object({
  id: z.number().optional(),
  city: z.string().trim().min(1, 'City is required').max(255),
  normalized_city: z.string().optional(),
  state: z.string().trim().min(1, 'State is required').max(32),
  country: z.string().trim().length(2).default(Country.UnitedStates.Value),
  display_name: z.string().nullish(),
  google_place_id: z.string().nullish(),
  fee: z.coerce.number().min(0, 'Delivery fee must be 0 or greater'),
  delivery_cutoff: nullableDeliveryCutoff.default(null),
})

export const deliveryDistanceFeeRangeSchema = z.object({
  id: z.number().optional(),
  min_miles: requiredNonnegativeNumber(
    'Start miles is required',
    'Start miles must be 0 or greater'
  ),
  max_miles: requiredNonnegativeNumber('End miles is required', 'End miles must be 0 or greater'),
  fee: z.coerce.number().min(0, 'Delivery fee must be 0 or greater'),
  delivery_cutoff: nullableDeliveryCutoff.default(null),
})

const locationHoursSchema = z.object({
  id: z.number().int().min(0).max(6),
  name: z.enum(daysOfWeek),
  open: z.string().regex(hourPattern, 'Open time must be in HH:mm format'),
  close: z.string().regex(hourPattern, 'Close time must be in HH:mm format'),
  is_open: z.boolean(),
})

const deliveryAreaFeeSchema = z.object({
  id: z.number().optional(),
  reference_id: z.string().optional(),
  encoded_polygon: z.string().min(1, 'Draw this area on the map before saving'),
  name: z.string().optional(),
  color: z.string().optional(),
  fee: z.preprocess(
    emptyNumberInputToUndefined,
    z.coerce.number().min(0, 'Delivery fee must be 0 or greater').nullish()
  ),
  delivery_cutoff: nullableDeliveryCutoff.default(null),
  polygon_type: z
    .enum([PolygonType.Base.Value, PolygonType.Location.Value, PolygonType.Excluded.Value])
    .nullable()
    .optional(),
  is_active: z.boolean().optional(),
  google_place_id: z.string().nullish(),
  place_display_name: z.string().nullish(),
  place_lat: z.coerce.number().min(-90).max(90).nullable().optional(),
  place_lng: z.coerce.number().min(-180).max(180).nullable().optional(),
})

const locationProfileShape = {
  address: locationAddressSchema,
  phone: z.string().optional(),
  phone_country_code: z.enum(countryValues).default(Country.UnitedStates.Value),
  email: z.email('Please enter a valid email').or(z.literal('')).optional(),
  google_business_profile_url: z
    .url('Please enter a valid URL')
    .max(2048)
    .or(z.literal(''))
    .refine(isGoogleBusinessProfileUrl, {
      message: 'Enter a Google Maps business profile or review request URL',
    })
    .optional(),
  is_active: z.boolean().default(true),
  is_default: z.boolean().default(false),
}

type LocationProfile = z.output<z.ZodObject<typeof locationProfileShape>>

const hasLocationContact = (data: Pick<LocationProfile, 'email' | 'phone'>) => {
  const hasEmail = data.email && data.email.trim() !== ''
  const hasPhone = data.phone && data.phone.trim() !== ''
  return hasEmail || hasPhone
}

const locationContactRequiredMessage = 'Either phone or email is required'

const addLocationContactValidation = (data: LocationProfile, ctx: z.RefinementCtx) => {
  if (hasLocationContact(data)) {
    return
  }

  ctx.addIssue({
    code: 'custom',
    path: ['phone'],
    message: locationContactRequiredMessage,
  })
  ctx.addIssue({
    code: 'custom',
    path: ['email'],
    message: locationContactRequiredMessage,
  })
}

const pickupSettingsShape = {
  is_pickup: z.boolean().default(false),
  pickup_cutoff: z.coerce.number().int().min(1).max(24).default(2),
  open_hours: z.array(locationHoursSchema).default([]),
}

const deliverySettingsShape = {
  is_delivery: z.boolean().default(false),
  delivery_cutoff: z.coerce.number().int().min(1).max(24).default(4),
  delivery_hours: z.array(locationHoursSchema).default([]),
  is_distance_pricing_enabled: z.boolean().default(false),
  is_delivery_cutoff_overrides_enabled: z.boolean().default(false),
  delivery_max_distance_miles: z.coerce.number().min(0).nullable().optional(),
  delivery_postal_code_fees: z.array(deliveryPostalCodeFeeSchema).default([]),
  delivery_city_fees: z.array(deliveryCityFeeSchema).default([]),
  delivery_distance_fee_ranges: z.array(deliveryDistanceFeeRangeSchema).default([]),
  delivery_area_fees: z.array(deliveryAreaFeeSchema).default([]),
}

const deliveryAvailabilitySettingsShape = {
  is_delivery: deliverySettingsShape.is_delivery,
  delivery_cutoff: deliverySettingsShape.delivery_cutoff,
  delivery_hours: deliverySettingsShape.delivery_hours,
}

const deliveryPricingSettingsShape = {
  is_distance_pricing_enabled: deliverySettingsShape.is_distance_pricing_enabled,
  is_delivery_cutoff_overrides_enabled: deliverySettingsShape.is_delivery_cutoff_overrides_enabled,
  delivery_max_distance_miles: deliverySettingsShape.delivery_max_distance_miles,
  delivery_postal_code_fees: deliverySettingsShape.delivery_postal_code_fees,
  delivery_city_fees: deliverySettingsShape.delivery_city_fees,
  delivery_distance_fee_ranges: deliverySettingsShape.delivery_distance_fee_ranges,
  delivery_area_fees: deliverySettingsShape.delivery_area_fees,
}

type PickupSettings = z.output<z.ZodObject<typeof pickupSettingsShape>>
type DeliverySettings = z.output<z.ZodObject<typeof deliverySettingsShape>>
type DeliveryAvailabilitySettings = z.output<z.ZodObject<typeof deliveryAvailabilitySettingsShape>>
/** `is_delivery` comes from the full delivery settings or the pricing schema's `isDeliveryEnabled`. */
type DeliveryPricingSettings = z.output<z.ZodObject<typeof deliveryPricingSettingsShape>> & {
  is_delivery?: boolean
}

const hasOpenDay = (hours: ReadonlyArray<{ is_open: boolean } | undefined> = []) =>
  hours.some((day) => day?.is_open)

const addDefaultLocationValidation = (data: LocationProfile, ctx: z.RefinementCtx) => {
  if (data.is_default && !data.is_active) {
    ctx.addIssue({
      code: 'custom',
      path: ['is_active'],
      message: 'A default location must be active',
    })
  }
}

const addCreateDefaultFulfillmentValidation = (data: LocationProfile, ctx: z.RefinementCtx) => {
  if (!data.is_default) {
    return
  }

  ctx.addIssue({
    code: 'custom',
    path: ['is_default'],
    message: 'Set up pickup or delivery before making this the default location',
  })
}

const addPickupSettingsValidation = (data: PickupSettings, ctx: z.RefinementCtx) => {
  if (!data.is_pickup) {
    return
  }

  if (!data.open_hours.length) {
    ctx.addIssue({
      code: 'custom',
      path: ['open_hours'],
      message: 'Store hours are required when pickup is enabled',
    })
  }

  if (!hasOpenDay(data.open_hours)) {
    ctx.addIssue({
      code: 'custom',
      path: ['open_hours'],
      message: 'At least one store day must be open when pickup is enabled',
    })
  }
}

const addDeliveryAvailabilitySettingsValidation = (
  data: DeliveryAvailabilitySettings,
  ctx: z.RefinementCtx
) => {
  if (!data.is_delivery) {
    return
  }

  if (!data.delivery_hours.length) {
    ctx.addIssue({
      code: 'custom',
      path: ['delivery_hours'],
      message: 'Delivery hours are required when delivery is enabled',
    })
  }

  if (!hasOpenDay(data.delivery_hours)) {
    ctx.addIssue({
      code: 'custom',
      path: ['delivery_hours'],
      message: 'At least one delivery day must be open when delivery is enabled',
    })
  }
}

const addDeliveryPricingSettingsValidation = (
  data: DeliveryPricingSettings,
  ctx: z.RefinementCtx
) => {
  const boundaryIndexes = data.delivery_area_fees
    .map((area, index) => ({ area, index }))
    .filter(({ area }) => area.polygon_type === PolygonType.Base.Value)
    .map(({ index }) => index)
  // Defined exactly when more than one boundary exists.
  const secondBoundaryIndex = boundaryIndexes[1]
  if (secondBoundaryIndex !== undefined) {
    ctx.addIssue({
      code: 'custom',
      path: ['delivery_area_fees', secondBoundaryIndex],
      message: 'Only one delivery boundary can be saved',
    })
  }

  data.delivery_area_fees.forEach((area, index) => {
    if (area.polygon_type === PolygonType.Location.Value && area.fee == null) {
      ctx.addIssue({
        code: 'custom',
        path: ['delivery_area_fees', index, 'fee'],
        message: 'Delivery fee is required for a special price place',
      })
    }

    if (area.polygon_type !== PolygonType.Location.Value && area.delivery_cutoff != null) {
      ctx.addIssue({
        code: 'custom',
        path: ['delivery_area_fees', index, 'delivery_cutoff'],
        message: 'Only special price places can have their own delivery cutoff',
      })
    }
  })

  if (data.is_delivery_cutoff_overrides_enabled) {
    const hasApplicableCutoff =
      data.delivery_postal_code_fees.some((fee) => fee.delivery_cutoff != null) ||
      data.delivery_city_fees.some((fee) => fee.delivery_cutoff != null) ||
      (data.is_distance_pricing_enabled &&
        data.delivery_distance_fee_ranges.some((range) => range.delivery_cutoff != null)) ||
      data.delivery_area_fees.some(
        (area) =>
          area.polygon_type === PolygonType.Location.Value &&
          area.is_active !== false &&
          area.delivery_cutoff != null
      )

    if (!hasApplicableCutoff) {
      ctx.addIssue({
        code: 'custom',
        path: ['is_delivery_cutoff_overrides_enabled'],
        message: 'Set at least one delivery cutoff before turning this on',
      })
    }
  }

  if (data.is_delivery === false) {
    return
  }

  const hasPostalFees = data.delivery_postal_code_fees.length > 0
  const hasCityFees = data.delivery_city_fees.length > 0

  if (!hasPostalFees && !hasCityFees) {
    ctx.addIssue({
      code: 'custom',
      path: ['delivery_postal_code_fees'],
      message: 'Add at least one ZIP code or city',
    })
  }

  if (hasPostalFees) {
    const postalCodeCounts = data.delivery_postal_code_fees.reduce((counts, fee) => {
      const postalCode = fee.postal_code.trim().toUpperCase()
      if (postalCode) {
        counts.set(postalCode, (counts.get(postalCode) || 0) + 1)
      }
      return counts
    }, new Map<string, number>())

    data.delivery_postal_code_fees.forEach((fee, index) => {
      const postalCode = fee.postal_code.trim().toUpperCase()
      if (postalCode && (postalCodeCounts.get(postalCode) || 0) > 1) {
        ctx.addIssue({
          code: 'custom',
          path: ['delivery_postal_code_fees', index, 'postal_code'],
          message: 'Duplicate postal code',
        })
      }
    })
  }

  if (hasCityFees) {
    const cityCounts = data.delivery_city_fees.reduce((counts, fee) => {
      const key = [
        fee.country?.trim().toUpperCase() || Country.UnitedStates.Value,
        fee.state.trim().toUpperCase(),
        fee.city.trim().toLowerCase().replace(/\s+/g, ' '),
      ].join('|')
      counts.set(key, (counts.get(key) || 0) + 1)
      return counts
    }, new Map<string, number>())

    data.delivery_city_fees.forEach((fee, index) => {
      const key = [
        fee.country?.trim().toUpperCase() || Country.UnitedStates.Value,
        fee.state.trim().toUpperCase(),
        fee.city.trim().toLowerCase().replace(/\s+/g, ' '),
      ].join('|')

      if ((cityCounts.get(key) || 0) > 1) {
        ctx.addIssue({
          code: 'custom',
          path: ['delivery_city_fees', index, 'city'],
          message: 'Duplicate city',
        })
      }
    })
  }

  if (data.is_distance_pricing_enabled) {
    const maxDistance = Number(data.delivery_max_distance_miles || 0)
    if (maxDistance <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['delivery_max_distance_miles'],
        message: 'Set a maximum driving distance',
      })
    }

    const ranges = data.delivery_distance_fee_ranges
      .map((range, index) => ({
        index,
        min: toFiniteNumber(range.min_miles),
        max: toFiniteNumber(range.max_miles),
      }))
      .sort((a, b) => Number(a.min ?? 0) - Number(b.min ?? 0))

    if (!ranges.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['delivery_distance_fee_ranges'],
        message: 'Add at least one driving distance fee range',
      })
    }

    if (!ranges.every(isCompleteDistanceRange)) {
      return
    }

    let expectedMin = 0
    ranges.forEach((range) => {
      const min = range.min
      const max = range.max

      if (Math.abs(min - expectedMin) > 0.009) {
        ctx.addIssue({
          code: 'custom',
          path: ['delivery_distance_fee_ranges', range.index, 'min_miles'],
          message: `Start this range at ${formatMiles(expectedMin)} miles so there is no gap or overlap`,
        })
      }

      if (max <= min) {
        ctx.addIssue({
          code: 'custom',
          path: ['delivery_distance_fee_ranges', range.index, 'max_miles'],
          message: 'End miles must be greater than start miles',
        })
      }

      expectedMin = max
    })

    if (ranges.length && maxDistance > 0 && Math.abs(expectedMin - maxDistance) > 0.009) {
      ctx.addIssue({
        code: 'custom',
        path: ['delivery_distance_fee_ranges'],
        message: 'Distance ranges must end at the maximum driving distance',
      })
    }
  }
}

const addDeliverySettingsValidation = (data: DeliverySettings, ctx: z.RefinementCtx) => {
  addDeliveryAvailabilitySettingsValidation(data, ctx)
  addDeliveryPricingSettingsValidation(data, ctx)
}

export const updateLocationProfileSchema = z
  .object(locationProfileShape)
  .superRefine(addLocationContactValidation)
  .superRefine(addDefaultLocationValidation)

export const locationPickupSettingsSchema = z
  .object(pickupSettingsShape)
  .superRefine(addPickupSettingsValidation)

export const locationDeliverySettingsSchema = z
  .object(deliverySettingsShape)
  .superRefine(addDeliverySettingsValidation)

export const locationDeliveryAvailabilitySettingsSchema = z
  .object(deliveryAvailabilitySettingsShape)
  .superRefine(addDeliveryAvailabilitySettingsValidation)

export const createLocationDeliveryPricingSettingsSchema = ({ isDeliveryEnabled = true } = {}) =>
  z.object(deliveryPricingSettingsShape).superRefine((data, ctx) => {
    addDeliveryPricingSettingsValidation({ ...data, is_delivery: isDeliveryEnabled }, ctx)
  })

export const locationDeliveryPricingSettingsSchema = createLocationDeliveryPricingSettingsSchema()

export const createLocationSchema = z
  .object(locationProfileShape)
  .superRefine(addLocationContactValidation)
  .superRefine(addDefaultLocationValidation)
  .superRefine(addCreateDefaultFulfillmentValidation)

// Backwards-compatible aliases.
export const locationSchema = createLocationSchema
export const updateLocationSchema = updateLocationProfileSchema
